import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getInvoice, updateInvoice, deleteInvoice, fetchInvoicePdfUrl,
  pushInvoiceToXero, markInvoiceSent, getXeroStatus,
  emailInvoice, previewInvoiceEmail, getEmailStatus,
  getUsers, detachWorkOrderFromInvoice, refreshInvoiceFromXero,
  renameInvoice, authoriseInvoice,
  applyDiscountsToInvoice, unapplyDiscountFromInvoice, discardPendingDiscountCredit,
  rebuildInvoiceFromSource, sendPaymentThankYou, setInvoiceVisibility,
} from '../../services/api';
import { PageHeader, StatusBadge } from '../../components/Shared';
import { useBreadcrumbOverride } from '../../context/BreadcrumbContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowLeft, Trash2, Save, Plus, Download, Lock, Pencil, Home, Building2, Package,
  Send, Upload, ExternalLink, Mail, Eye, EyeOff, Unlink2, RefreshCw, History, Clock, CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { toast } from 'sonner';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import RecipientStack from '../../components/RecipientStack';

/**
 * Draft-invoice detail page.
 *  · Lists every line item with inline editable description / quantity /
 *    unit amount.
 *  · "Remove line" per row + "Add line" footer button.
 *  · Total auto-recomputes as the admin edits.
 *  · Save writes the entire `lineItems[]` array back in one request;
 *    backend recomputes `amount` server-side from the same fields so
 *    client & DB can never disagree.
 *  · Delete removes the draft and un-stamps every source row
 *    (time entries + WO parts) so they become re-billable.
 *  · Sent/locked invoices render read-only with a hint banner.
 */

// Format an ISO timestamp (e.g. `2026-05-02T16:42:11.123456+00:00`) for
// the "Last emailed: ..." stamp. Falls back to the raw string if Date
// parsing fails — better than crashing the whole header.
const formatEmailedAt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Compact "To: chip chip chip" / "Cc: chip chip chip" row used inside
// the email dialog so the admin can see at a glance who's getting the
// invoice and remove anyone with one tap.
//
// Now extracted to /components/RecipientStack.jsx so the same UI is
// used by the "Chase overdue" dialog on the customer detail page.

const AdminInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const pdf = usePdfPreview();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Rename-draft-invoice dialog state. Only meaningful while
  // `isDraft && !isPushed`. The new id is then cascaded across all
  // FK references on the backend.
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailNote, setEmailNote] = useState('');
  // Recipient state: a TO list, a CC list, plus a manual-entry pair
  // (input + last-clicked target). The legacy `emailRecipient` /
  // `emailCcAdmin` flags are kept so the persisted defaults still work.
  const [emailTo, setEmailTo] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  const [emailManual, setEmailManual] = useState('');
  const [emailCcAdmin, setEmailCcAdmin] = useState(true);
  const [emailPushFirst, setEmailPushFirst] = useState(true);
  const [emailLockAfter, setEmailLockAfter] = useState(true);
  const [emailSending, setEmailSending] = useState(false);
  // Email preview dialog — opened from the email-send dialog so the
  // admin can eyeball EXACTLY the subject + body the customer will
  // receive AND the PDF that will be attached, before clicking Send.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);   // { subject, html, sender, adminCc, ... }
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfName, setPreviewPdfName] = useState('');
  // "Edit body" toggle on the preview dialog. When `editingBody` is
  // true we swap the iframe for a PLAIN-TEXT textarea seeded from the
  // preview endpoint's `defaultBodyText`. The admin edits normal
  // paragraphs — no HTML tags in sight — and the server wraps them
  // back into the branded template on send. `editedBodyText` is the
  // source of truth; when it differs from the default we send it as
  // `body_text`.
  const [editingBody, setEditingBody] = useState(false);
  const [editedBodyText, setEditedBodyText] = useState('');
  // Extra editable fields exposed on the preview dialog. Each falls
  // back to the server-provided default when left blank — that way
  // partial edits are safe.
  const [editedSubject, setEditedSubject] = useState('');
  const [editedGreetingLine, setEditedGreetingLine] = useState('');
  const [editedButtonLabel, setEditedButtonLabel] = useState('');
  const [editedCloser, setEditedCloser] = useState('');
  // Team email contacts for the invoice's customer (parent + any
  // `parentCustomerId == customerId` sub-users). Loaded lazily when the
  // email dialog opens so the page itself stays snappy.
  const [emailContacts, setEmailContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  // Detach-WO confirmation. Set to a workOrderId when the admin clicks
  // the "Detach" pill above a WO group of line items.
  const [detachWoId, setDetachWoId] = useState(null);
  const [detaching, setDetaching] = useState(false);

  // Group lineItems by workOrderId for the detach UI. Lines without a
  // workOrderId fall under the empty-string key so they still render as
  // a single ungrouped block.
  const lineGroups = useMemo(() => {
    const map = new Map();
    lineItems.forEach((li, idx) => {
      const key = li.workOrderId || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ li, idx });
    });
    return Array.from(map.entries()); // [[woId, [{li, idx}, ...]], ...]
  }, [lineItems]);

  const detachWO = async (woId) => {
    setDetaching(true);
    try {
      const res = await detachWorkOrderFromInvoice(invoiceId, woId);
      const fresh = res.invoice;
      if (fresh) {
        setInvoice(fresh);
        setLineItems(fresh.lineItems || []);
      }
      setDetachWoId(null);
      toast.success(`${woId} detached · ${res.linesRemoved} line${res.linesRemoved !== 1 ? 's' : ''} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detach failed');
    } finally {
      setDetaching(false);
    }
  };

  const [notFound, setNotFound] = useState(false);
  // uid we just redirected to — skip the duplicate fetch it would trigger.
  const loadedUidRef = useRef(null);

  useEffect(() => {
    if (loadedUidRef.current && loadedUidRef.current === invoiceId) {
      loadedUidRef.current = null; // one-shot: later visits to this uid must refetch
      return;
    }
    loadedUidRef.current = null;
    setLoading(true);
    setNotFound(false);
    getInvoice(invoiceId)
      .then((inv) => {
        // Canonicalise the URL to the permanent id (old numbers still resolve).
        if (inv.uid && inv.uid !== invoiceId) {
          loadedUidRef.current = inv.uid;
          navigate(`/admin/invoices/${inv.uid}`, { replace: true });
        }
        setInvoice(inv);
        setLineItems(inv.lineItems || []);
        setDueDate(inv.dueDate || '');
        setNotes(inv.notes || '');
        setDirty(false);
      })
      .catch((e) => {
        // 404 = orphan reference (the WO points at a deleted invoice).
        // Don't silently bounce the admin back to the list — show a
        // proper "not found" surface so they understand WHY the link
        // led nowhere and can either jump back, or detach the orphan
        // reference from the WO.
        const status = e?.response?.status;
        if (status === 404) {
          setNotFound(true);
        } else {
          toast.error(e?.response?.data?.detail || 'Failed to load invoice');
          navigate('/admin/invoices');
        }
      })
      .finally(() => setLoading(false));
  }, [invoiceId, navigate]);

  useEffect(() => {
    getXeroStatus().then((s) => setXeroConnected(!!s?.connected)).catch(() => {});
    getEmailStatus().then((s) => setEmailConfigured(!!s?.configured)).catch(() => {});
  }, []);

  // Clean up the preview PDF blob URL on unmount — these can hold
  // megabytes per leaked URL on long sessions.
  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        try { URL.revokeObjectURL(previewPdfUrl); } catch { /* noop */ }
      }
    };
  }, [previewPdfUrl]);

  const isDraft = invoice
    && !invoice.sent
    && (
      (invoice.status || '').toLowerCase() === 'draft'
      || (invoice.xeroStatus || '') === 'DRAFT'
    );
  const isPushed = !!(invoice && invoice.xeroInvoiceId);
  const xeroNumber = invoice?.xeroInvoiceNumber || '';
  // Push the friendlier Xero invoice number (e.g. "IN202600025") into
  // the top-bar breadcrumb so the internal Mongo id (e.g. "INV-2815")
  // doesn't also surface in the UI.
  useBreadcrumbOverride(xeroNumber || invoice?.id || null);

  // Live totals — mirrors the server-side recompute so the admin sees
  // the final figures before they hit Save.
  //
  // All prices on the app are stored NET (pre-VAT) — this includes
  // fixed prices on WOs, hourly rates on the rate card, and call-out
  // fees. So we compute the VAT stack per line, then sum:
  //   subtotal = Σ net       (net = qty × unit)
  //   vatTotal = Σ net × taxRate (per-line rate; default 20% if missing)
  //   grandTotal = subtotal + vatTotal
  const DEFAULT_TAX_RATE = 0.20;
  const lineTotals = useMemo(() => lineItems.map((li) => {
    const net = (Number(li.quantity) || 0) * (Number(li.unit_amount) || 0);
    // Falsy check on `taxRate` intentionally lets 0 through — a
    // zero-rated line is meaningful. Only apply the 20% default when
    // the field is truly missing.
    const rate = (li.taxRate === undefined || li.taxRate === null || li.taxRate === '')
      ? DEFAULT_TAX_RATE
      : Number(li.taxRate) || 0;
    const vat = net * rate;
    return { net, vat, gross: net + vat, rate };
  }), [lineItems]);
  const totals = useMemo(() => {
    const subtotal = lineTotals.reduce((s, t) => s + t.net, 0);
    const vatTotal = lineTotals.reduce((s, t) => s + t.vat, 0);
    return { subtotal, vatTotal, grandTotal: subtotal + vatTotal };
  }, [lineTotals]);
  // Kept as an alias so the rest of the file that still reads
  // `computedTotal` keeps working — this always reflects the GROSS
  // now (what the customer actually pays).
  const computedTotal = totals.grandTotal;

  const updateLine = (idx, patch) => {
    setLineItems((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  const removeLine = (idx) => {
    if (!window.confirm('Remove this line from the draft?')) return;
    setLineItems((ls) => ls.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addLine = () => {
    setLineItems((ls) => [
      ...ls,
      {
        type: 'labor',
        description: '',
        quantity: 1,
        unit_amount: 0,
        taxRate: DEFAULT_TAX_RATE,
        account_code: '200',
        xeroItemCode: '',
        timeEntryId: '',
        workOrderId: '',
        partId: '',
        rateCardRuleId: '',
      },
    ]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateInvoice(invoiceId, { lineItems, dueDate, notes });
      setInvoice(res);
      setLineItems(res.lineItems || []);
      setDirty(false);
      toast.success(`Draft saved · £${(res.amount || 0).toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeDraft = async () => {
    if (!window.confirm(
      `Delete draft ${invoice.id}? This un-stamps every time entry + part that fed it so they become re-billable. Cannot be undone.`,
    )) return;
    setDeleting(true);
    try {
      await deleteInvoice(invoiceId);
      toast.success(`Draft ${invoice.id} deleted`);
      navigate('/admin/invoices?status=draft');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      setDeleting(false);
    }
  };

  // Rename a draft invoice's number — both the local id AND the Xero
  // number are updated, and every FK reference (work_orders.invoiceId,
  // time_entries.billedInvoiceId, partsUsed[].billedInvoiceId) is
  // cascaded server-side. On success we navigate to the new id so the
  // URL stays consistent with the new identity.
  const openRename = () => {
    setRenameValue(invoice?.xeroInvoiceNumber || invoice?.id || '');
    setRenameOpen(true);
  };
  const submitRename = async () => {
    const trimmed = (renameValue || '').trim();
    if (!trimmed) { toast.error('Enter the new invoice number'); return; }
    if (trimmed === invoice.id) { setRenameOpen(false); return; }
    if (!/^[A-Za-z0-9_-]{1,60}$/.test(trimmed)) {
      toast.error('Use 1–60 letters / digits / dash / underscore only');
      return;
    }
    setRenameSaving(true);
    try {
      const res = await renameInvoice(invoice.id, trimmed);
      const cascaded = res?.cascaded || {};
      const total = (cascaded.workOrders || 0) + (cascaded.timeEntries || 0) + (cascaded.partUsages || 0);
      toast.success(
        total > 0
          ? `Renumbered to ${trimmed} · ${total} reference${total === 1 ? '' : 's'} updated`
          : `Renumbered to ${trimmed}`,
      );
      setRenameOpen(false);
      // URL uses the permanent uid, so just swap in the renamed record.
      if (res?.invoice) setInvoice(res.invoice);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally {
      setRenameSaving(false);
    }
  };

  // Save any pending edits, then push the draft to Xero. Merges the
  // returned xeroInvoiceId/Number/Status into local state so the button
  // row re-renders without a navigation.
  const pushToXero = async () => {
    if (dirty) {
      if (!window.confirm('You have unsaved changes. Save before pushing to Xero?')) return;
      await save();
    }
    if (pushing) return;
    setPushing(true);
    try {
      const res = await pushInvoiceToXero(invoice?.uid || invoiceId);
      // Response carries the renumbered record — no refetch by old number.
      const fresh = res?.invoice || (await getInvoice(invoice?.uid || invoiceId));
      setInvoice(fresh);
      setLineItems(fresh.lineItems || []);
      setDueDate(fresh.dueDate || '');
      setNotes(fresh.notes || '');
      setDirty(false);
      const num = res?.xeroInvoiceNumber || fresh.xeroInvoiceNumber || fresh.id;
      toast.success(
        res?.recovered
          ? `Reconnected to existing Xero invoice ${num} (${res?.xeroStatus || 'DRAFT'}) — no duplicate created`
          : `Pushed to Xero as ${num} (${res?.xeroStatus || fresh.xeroStatus || 'DRAFT'})`,
        { duration: 6000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push to Xero failed');
    } finally {
      setPushing(false);
    }
  };

  // Pull the latest line items, totals, dates and status from Xero
  // for THIS invoice. Lets admins immediately see edits they just made
  // in Xero without waiting for the full periodic sync to run.
  const refreshFromXero = async () => {
    if (dirty) {
      if (!window.confirm('Refreshing will discard your unsaved local changes. Continue?')) return;
    }
    setRefreshing(true);
    try {
      const res = await refreshInvoiceFromXero(invoiceId);
      if (res?.voided) {
        toast.warning(res.detail || 'Invoice was voided in Xero — removed from portal.');
        navigate('/admin/invoices');
        return;
      }
      const fresh = res?.invoice || (await getInvoice(invoiceId));
      setInvoice(fresh);
      setLineItems(fresh.lineItems || []);
      setDueDate(fresh.dueDate || '');
      setNotes(fresh.notes || '');
      setDirty(false);
      const num = fresh.xeroInvoiceNumber || fresh.id;
      toast.success(`${num} synced from Xero`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh from Xero failed');
    } finally {
      setRefreshing(false);
    }
  };

  // Approve = flip Xero invoice status DRAFT → AUTHORISED. Pure status
  // change, leaves the local `sent` flag alone — admin can still
  // re-send and re-edit afterwards. Only meaningful when the invoice
  // is already in Xero AND currently DRAFT.
  const [approving, setApproving] = useState(false);
  const approveInvoice = async () => {
    const num = xeroNumber || invoice.id;
    if (!window.confirm(`Approve ${num}? This authorises it in Xero so it leaves the Drafts tab and a "Review and pay" link is issued.`)) return;
    setApproving(true);
    try {
      const res = await authoriseInvoice(invoiceId);
      const fresh = res?.invoice || await getInvoice(invoiceId);
      setInvoice(fresh);
      setLineItems(fresh.lineItems || []);
      toast.success(`${num} authorised in Xero`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to authorise invoice');
    } finally {
      setApproving(false);
    }
  };

  const markSent = async () => {
    const num = xeroNumber || invoice.id;
    const pushStatus = isPushed;
    const msg = pushStatus
      ? `Lock invoice ${num} and mark it AUTHORISED in Xero? Future work orders for ${invoice.customer} will start a new invoice.`
      : `Lock invoice ${num} locally? It will stop accumulating new lines, but won't be pushed to Xero. Future work orders for ${invoice.customer} will start a new invoice.`;
    if (!window.confirm(msg)) return;
    setSending(true);
    try {
      await markInvoiceSent(invoiceId, pushStatus);
      const fresh = await getInvoice(invoiceId);
      setInvoice(fresh);
      setLineItems(fresh.lineItems || []);
      setDirty(false);
      toast.success(pushStatus ? `${num} locked & AUTHORISED in Xero` : `${num} locked locally`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to lock invoice');
    } finally {
      setSending(false);
    }
  };

  // One-click: (optionally push to Xero) → render PDF → email via Resend
  // → (optionally lock). Pre-fills the recipient from invoice.customerEmail
  // when available, otherwise backend resolves from the customer record.
  const openEmailDialog = () => {
    if (dirty) {
      if (!window.confirm('You have unsaved changes. Save them before emailing?')) return;
      save();
    }
    // Pre-fill TO with the customer's primary email (if known); admin
    // can add/move chips before sending. If the invoice doc doesn't
    // carry a `customerEmail` we top-it-up from the roster below the
    // moment it loads — otherwise the TO field would start empty and
    // the admin might auto-complete a sub-customer's email by mistake.
    setEmailTo(invoice.customerEmail ? [invoice.customerEmail] : []);
    setEmailCc([]);
    setEmailManual('');
    setEmailNote('');
    setEmailCcAdmin(true);
    setEmailPushFirst(xeroConnected && !isPushed);
    setEmailLockAfter(!invoice.sent);
    setEmailOpen(true);
    // If the contact roster is already loaded from a previous open,
    // auto-seed the TO field with the parent (isPrimary) contact —
    // otherwise re-opens would start with an empty TO and the admin
    // might auto-complete a sub-customer's email.
    if (contactsLoaded && emailContacts.length > 0 && !invoice.customerEmail) {
      const primary = emailContacts.find((c) => c.isPrimary);
      if (primary) setEmailTo([primary.email]);
    }
    if (!contactsLoaded && invoice.customerId) {
      getUsers().then((users) => {
        // Walk UP the parentCustomerId chain to find the true
        // top-level customer for this invoice. Then load the roster
        // relative to THAT so admins see the parent + all siblings,
        // not just the sub-customer whose id happens to be on the
        // invoice doc.
        const byId = new Map(users.map((u) => [u.id, u]));
        let anchor = byId.get(invoice.customerId) || null;
        let steps = 0;
        while (anchor && anchor.parentCustomerId && steps < 6) {
          const p = byId.get(anchor.parentCustomerId);
          if (!p || p.id === anchor.id) break;
          anchor = p;
          steps += 1;
        }
        const anchorId = (anchor && anchor.id) || invoice.customerId;

        // Two sources of contacts to merge:
        //  1. `users` docs with role='customer' whose id === anchor,
        //     OR whose parentCustomerId === anchor (sub-customers).
        //     These give the parent + sub-companies' primary emails.
        //  2. Each of those customers' embedded `contacts[]` array —
        //     the human contacts admins add via ContactAutocomplete
        //     (Dave the site manager, etc.). These are NOT separate
        //     users, so a naive roster misses them entirely — that's
        //     why the admin was seeing only "info@cammell-lairds".
        const inScope = users.filter((u) => (
          u.role === 'customer'
          && (u.id === anchorId || u.parentCustomerId === anchorId)
        ));
        const seen = new Set();
        const ordered = [];
        const push = (row) => {
          const key = (row.email || '').trim().toLowerCase();
          if (!key || seen.has(key)) return;
          seen.add(key);
          ordered.push(row);
        };
        for (const u of inScope) {
          push({
            id: u.id,
            email: (u.email || '').trim(),
            name: u.company || u.name || '',
            isPrimary: u.id === anchorId,
            source: 'customer',
          });
          for (const sub of (u.contacts || [])) {
            const label = [sub.name, sub.role || sub.title]
              .filter(Boolean).join(' · ');
            const suffix = (u.id === anchorId)
              ? ''
              : ` @ ${u.company || u.name}`;   // sub-customer context
            push({
              id: `${u.id}:${(sub.email || '').toLowerCase()}`,
              email: (sub.email || '').trim(),
              name: (label + suffix) || sub.email || '',
              isPrimary: false,
              source: 'contact',
            });
          }
        }
        // Primary first, then everything else in insertion order.
        ordered.sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
        setEmailContacts(ordered);
        setContactsLoaded(true);
        setEmailTo((prev) => {
          if (prev.length > 0) return prev;
          const primary = ordered.find((c) => c.isPrimary);
          return primary ? [primary.email] : prev;
        });
      }).catch(() => setContactsLoaded(true));
    }
  };

  // ---------- Helpers for the TO / CC chip pickers ----------
  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

  // Move (or add) an email into the named bucket. Removes from the
  // other bucket so an address never lands in both. Bucket = 'to' | 'cc'.
  const assignEmail = (email, bucket) => {
    const trimmed = (email || '').trim();
    if (!isValidEmail(trimmed)) {
      toast.error('Enter a valid email');
      return;
    }
    const lower = trimmed.toLowerCase();
    setEmailTo((prev) => prev.filter((e) => e.toLowerCase() !== lower).concat(bucket === 'to' ? [trimmed] : []));
    setEmailCc((prev) => prev.filter((e) => e.toLowerCase() !== lower).concat(bucket === 'cc' ? [trimmed] : []));
  };

  const removeEmail = (email) => {
    const lower = (email || '').toLowerCase();
    setEmailTo((prev) => prev.filter((e) => e.toLowerCase() !== lower));
    setEmailCc((prev) => prev.filter((e) => e.toLowerCase() !== lower));
  };

  // What bucket is `email` currently assigned to? '' = neither.
  const emailBucket = (email) => {
    const lower = (email || '').toLowerCase();
    if (emailTo.some((e) => e.toLowerCase() === lower)) return 'to';
    if (emailCc.some((e) => e.toLowerCase() === lower)) return 'cc';
    return '';
  };

  const addManual = (bucket) => {
    if (!emailManual.trim()) return;
    assignEmail(emailManual, bucket);
    setEmailManual('');
  };

  /**
   * Derive the "Hi X," greeting name from the first TO chip. Looks the
   * email up in the contact roster and pulls the first word of the
   * contact's `name`. Falls back to the local-part of the email (bit
   * before the `@`) when there's no roster hit — better than the
   * default customer-company name in nearly every case.
   */
  const deriveGreetingName = () => {
    const target = (emailTo[0] || '').trim().toLowerCase();
    if (!target) return '';
    const hit = emailContacts.find((c) => (c.email || '').toLowerCase() === target);
    // Contact "name" fields sometimes have a role suffix ("Dave · site
    // manager @ Sub Corp"). Split on the ` · ` separator we insert
    // ourselves, then take the first word of the human name.
    const humanName = hit ? (hit.name || '').split('·')[0].trim() : '';
    if (humanName) {
      const first = humanName.split(/\s+/)[0];
      if (first) return first;
    }
    // Fallback: local part of the email, title-cased if it looks like a name.
    const local = target.split('@')[0];
    const looksLikeName = /^[a-z]+$/.test(local);
    if (looksLikeName) return local.charAt(0).toUpperCase() + local.slice(1);
    return '';
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    setEditingBody(false);
    setEditedBodyText('');
    if (previewPdfUrl) {
      try { URL.revokeObjectURL(previewPdfUrl); } catch { /* noop */ }
      setPreviewPdfUrl(null);
    }
    try {
      const [body, pdf] = await Promise.all([
        previewInvoiceEmail(invoiceId, {
          note: emailNote.trim(),
          cc_admin: emailCcAdmin,
          greeting_name: deriveGreetingName(),
        }),
        fetchInvoicePdfUrl(invoiceId).catch(() => null),
      ]);
      setPreviewData(body);
      setEditedBodyText(body.defaultBodyText || '');
      setEditedSubject(body.subject || body.defaultSubject || '');
      // Prefill the greeting input with the CURRENT greeting so the
      // admin can see what the customer would receive and rewrite it
      // if the auto-derived first name is wrong (common on companies
      // like "Invoices" or on shared inboxes).
      setEditedGreetingLine(body.defaultGreeting || '');
      setEditedButtonLabel(body.defaultButtonLabel || '');
      setEditedCloser(body.defaultCloser || '');
      if (pdf) {
        setPreviewPdfUrl(pdf.url);
        setPreviewPdfName(pdf.filename || `${invoiceId}.pdf`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewPdfUrl) {
      try { URL.revokeObjectURL(previewPdfUrl); } catch { /* noop */ }
    }
    setPreviewPdfUrl(null);
    setPreviewPdfName('');
    setPreviewData(null);
    setEditingBody(false);
    setEditedBodyText('');
    setEditedSubject('');
    setEditedGreetingLine('');
    setEditedButtonLabel('');
    setEditedCloser('');
  };

  // Live re-render of the preview HTML whenever the admin's edited
  // body text changes. Debounced through a small effect so we don't
  // hammer the backend on every keystroke.
  useEffect(() => {
    if (!previewOpen || !previewData || !editingBody) return;
    const handle = setTimeout(() => {
      previewInvoiceEmail(invoiceId, {
        note: emailNote.trim(),
        cc_admin: emailCcAdmin,
        greeting_name: deriveGreetingName(),
        body_text: editedBodyText === (previewData.defaultBodyText || '') ? '' : editedBodyText,
        subject: editedSubject === (previewData.defaultSubject || '') ? '' : editedSubject,
        greeting_line: editedGreetingLine === (previewData.defaultGreeting || '') ? '' : editedGreetingLine,
        button_label: editedButtonLabel === (previewData.defaultButtonLabel || '') ? '' : editedButtonLabel,
        closer: editedCloser === (previewData.defaultCloser || '') ? '' : editedCloser,
      }).then((body) => {
        setPreviewData((prev) => prev ? { ...prev, html: body.html, subject: body.subject } : body);
      }).catch(() => { /* silent — keep old preview */ });
    }, 500);
    return () => clearTimeout(handle);
  }, [editedBodyText, editedSubject, editedGreetingLine, editedButtonLabel, editedCloser, editingBody]);

  const sendEmail = async () => {
    if (emailTo.length === 0) {
      toast.error('Add at least one TO recipient');
      return;
    }
    setEmailSending(true);
    try {
      // If the admin edited the body text in the preview dialog, send
      // it as `body_text`; server keeps the branded template intact
      // and only swaps the middle paragraphs.
      const bodyTextOverride = (editedBodyText && editedBodyText !== (previewData?.defaultBodyText || ''))
        ? editedBodyText
        : '';
      const subjectOverride = (editedSubject && editedSubject !== (previewData?.defaultSubject || ''))
        ? editedSubject
        : '';
      const buttonLabelOverride = (editedButtonLabel && editedButtonLabel !== (previewData?.defaultButtonLabel || ''))
        ? editedButtonLabel
        : '';
      const closerOverride = (editedCloser && editedCloser !== (previewData?.defaultCloser || ''))
        ? editedCloser
        : '';
      const greetingLineOverride = (editedGreetingLine && editedGreetingLine !== (previewData?.defaultGreeting || ''))
        ? editedGreetingLine
        : '';
      const res = await emailInvoice(invoiceId, {
        to_list: emailTo,
        cc: emailCc,
        cc_admin: emailCcAdmin,
        note: emailNote.trim(),
        push_to_xero_first: emailPushFirst,
        lock_after_send: emailLockAfter,
        greeting_name: deriveGreetingName(),
        body_text: bodyTextOverride,
        subject: subjectOverride,
        greeting_line: greetingLineOverride,
        button_label: buttonLabelOverride,
        closer: closerOverride,
      });
      const fresh = await getInvoice(invoiceId);
      setInvoice(fresh);
      setLineItems(fresh.lineItems || []);
      setDirty(false);
      setEmailOpen(false);
      const extra = [
        res?.pushedNow ? 'pushed to Xero' : null,
        res?.pdfSource === 'xero' ? 'Xero PDF' : 'Lutrom PDF',
        res?.locked ? 'locked' : null,
        (res?.cc || []).length ? `cc ${(res.cc || []).length}` : null,
      ].filter(Boolean).join(' · ');
      const toLabel = (res?.to || []).join(', ');
      toast.success(`Sent to ${toLabel} · ${extra}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="invoice-not-found">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="text-rose-600 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div className="flex-1">
              <div className="font-semibold text-rose-900">
                Invoice <span className="font-mono">{invoiceId}</span> not found
              </div>
              <div className="text-sm text-rose-800 mt-1">
                The work order or link you followed references this invoice,
                but no matching invoice exists in the database. It may have been
                deleted, or it was never created.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={() => navigate('/admin/invoices')}
              variant="outline"
              data-testid="invoice-not-found-back"
            >
              Back to invoices list
            </Button>
            <Button
              onClick={() => navigate('/admin/work-orders')}
              variant="outline"
              data-testid="invoice-not-found-wo"
            >
              Open work orders
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (!invoice) return null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/invoices?status=draft"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-2"
          data-testid="back-to-invoices"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to invoices
        </Link>
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>{xeroNumber ? `Invoice ${xeroNumber}` : invoice.id}</span>
              {isDraft && (
                <button
                  type="button"
                  onClick={openRename}
                  data-testid="invoice-rename-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 transition-colors"
                  title={isPushed ? 'Renumber this invoice (will be pushed to Xero too)' : 'Renumber this draft (cascades through every FK)'}
                >
                  <Pencil className="h-3 w-3" />
                  Renumber
                </button>
              )}
              {/* Hide-from-customer toggle. When ON, the customer
                  portal filters this invoice out of the list AND
                  serves 404 on the detail + PDF endpoints. Great
                  for internal-only records the admin needs to
                  register but doesn't want the customer to see. */}
              <button
                type="button"
                onClick={async () => {
                  const next = !invoice.hiddenFromCustomer;
                  const label = next ? 'Hide this invoice from the customer portal?' : 'Make this invoice visible to the customer again?';
                  if (!window.confirm(label)) return;
                  try {
                    await setInvoiceVisibility(invoice.id, next);
                    const fresh = await getInvoice(invoice.id);
                    setInvoice(fresh);
                    toast.success(next ? 'Hidden from customer.' : 'Visible to customer.');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not update visibility');
                  }
                }}
                data-testid="invoice-hide-toggle"
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  invoice.hiddenFromCustomer
                    ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
                title={invoice.hiddenFromCustomer
                  ? 'Currently HIDDEN from the customer portal. Click to make visible.'
                  : 'Currently visible to the customer. Click to hide.'}
              >
                {invoice.hiddenFromCustomer ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {invoice.hiddenFromCustomer ? 'Hidden from customer' : 'Visible to customer'}
              </button>
            </span>
          }
          subtitle={`${invoice.customer} · issued ${invoice.issuedAt || 'draft'}`}
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <StatusBadge status={invoice.status} />
        {isPushed && invoice.xeroStatus && (
          <Badge variant="outline" className="text-[11px] uppercase tracking-wider border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="xero-status-badge">
            Xero · {invoice.xeroStatus}
          </Badge>
        )}
        {invoice.sent && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
            <Lock className="h-3.5 w-3.5" /> Sent — read-only
          </span>
        )}
        {invoice.lastEmailedAt && (() => {
          // Roll up the delivery state of the most recent send so the
          // pill gets a colour: red on bounce/spam, green when opened,
          // sky when delivered, emerald default for "send happened".
          const last = (invoice.emailHistory || []).slice(-1)[0]?.delivery || {};
          let cls = 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800';
          if (last.bounced || last.complained) cls = 'border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800';
          else if (last.opened) cls = 'border-emerald-300 bg-emerald-100 hover:bg-emerald-200 text-emerald-900';
          else if (last.delivered) cls = 'border-sky-200 bg-sky-50 hover:bg-sky-100 text-sky-800';
          return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="email-history-trigger"
                className={`inline-flex items-center gap-1.5 rounded-full border ${cls} px-2.5 py-1 text-[11px] font-medium transition-colors`}
                title="Email send history"
              >
                <Clock className="h-3 w-3" />
                Last emailed{' '}
                <span className="font-mono">{formatEmailedAt(invoice.lastEmailedAt)}</span>
                {Array.isArray(invoice.emailHistory) && invoice.emailHistory.length > 1 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-emerald-700 text-white text-[10px] font-semibold h-4 min-w-[16px] px-1">
                    {invoice.emailHistory.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-96 max-w-[92vw] p-0 max-h-[60vh] overflow-y-auto"
              data-testid="email-history-popover"
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <History className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-900">Email history</span>
                <span className="ml-auto text-[11px] text-slate-500">
                  {(invoice.emailHistory || []).length || 1} send{((invoice.emailHistory || []).length || 1) === 1 ? '' : 's'}
                </span>
              </div>
              <ul className="divide-y divide-slate-100">
                {(invoice.emailHistory && invoice.emailHistory.length > 0
                  ? [...invoice.emailHistory].reverse()
                  : [{
                      at: invoice.lastEmailedAt,
                      to: (invoice.lastEmailedTo || '').split(',').map(s => s.trim()).filter(Boolean),
                      cc: (invoice.lastEmailedCc || '').split(',').map(s => s.trim()).filter(Boolean),
                      byUserName: '',
                      pdfSource: invoice.xeroInvoiceId ? 'xero' : 'local',
                    }]
                ).map((entry, idx) => (
                  <li key={idx} className="px-4 py-3 text-xs text-slate-700 space-y-1" data-testid={`email-history-entry-${idx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-slate-900">{formatEmailedAt(entry.at)}</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        {entry.wasResend && (
                          <span className="rounded-sm bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">re-send</span>
                        )}
                        {entry.pdfSource && (
                          <span className="rounded-sm bg-slate-50 text-slate-600 ring-1 ring-slate-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider">{entry.pdfSource} pdf</span>
                        )}
                        {entry.delivery?.bounced && (
                          <span className="rounded-sm bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider" title="Email bounced">bounced</span>
                        )}
                        {entry.delivery?.complained && (
                          <span className="rounded-sm bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider" title="Marked as spam">spam</span>
                        )}
                        {entry.delivery?.delayed && !entry.delivery?.delivered && (
                          <span className="rounded-sm bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider" title="Delivery delayed">delayed</span>
                        )}
                        {entry.delivery?.opened && (
                          <span className="rounded-sm bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider inline-flex items-center gap-0.5" title="Recipient opened">
                            <Eye className="h-2.5 w-2.5" /> opened
                          </span>
                        )}
                        {entry.delivery?.delivered && !entry.delivery?.opened && !entry.delivery?.bounced && !entry.delivery?.complained && (
                          <span className="rounded-sm bg-sky-50 text-sky-700 ring-1 ring-sky-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wider" title="Delivered to inbox">delivered</span>
                        )}
                      </div>
                    </div>
                    {(entry.to || []).length > 0 && (
                      <div className="leading-snug">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">To </span>
                        <span className="break-all">{(entry.to || []).join(', ')}</span>
                      </div>
                    )}
                    {(entry.cc || []).length > 0 && (
                      <div className="leading-snug">
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">Cc </span>
                        <span className="break-all">{(entry.cc || []).join(', ')}</span>
                      </div>
                    )}
                    {entry.byUserName && (
                      <div className="text-[10px] text-slate-500">by {entry.byUserName}</div>
                    )}
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
          );
        })()}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => pdf.preview(() => fetchInvoicePdfUrl(invoice.id))}
            disabled={pdf.busy}
            data-testid="invoice-preview-pdf-btn"
          >
            <Eye className="h-4 w-4 mr-1" /> Preview PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => pdf.download(() => fetchInvoicePdfUrl(invoice.id))}
            data-testid="invoice-download-pdf-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
          {isDraft && (
            <Button
              variant="outline"
              className="text-red-700 border-red-300 hover:bg-red-50"
              onClick={removeDraft}
              disabled={deleting}
              data-testid="delete-draft-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting…' : 'Delete draft'}
            </Button>
          )}
          {isDraft && (
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          )}
          {isDraft && xeroConnected && (
            <Button
              onClick={pushToXero}
              disabled={pushing || saving || lineItems.length === 0}
              className="bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="push-to-xero-btn"
            >
              <Upload className="h-4 w-4 mr-1" />
              {pushing ? 'Pushing…' : isPushed ? 'Update in Xero' : 'Push to Xero'}
            </Button>
          )}
          {emailConfigured && lineItems.length > 0
            && (invoice.status || '').toLowerCase() !== 'paid'
            && (invoice.status || '').toLowerCase() !== 'voided'
            && (invoice.xeroStatus || '') !== 'PAID'
            && (invoice.xeroStatus || '') !== 'VOIDED' && (
            <Button
              onClick={openEmailDialog}
              disabled={emailSending || lineItems.length === 0}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="email-invoice-btn"
            >
              <Mail className="h-4 w-4 mr-1" /> {invoice.sent ? 'Re-send to customer' : 'Email to customer'}
            </Button>
          )}
          {isPushed && (invoice.xeroStatus || '') === 'DRAFT' && (
            <Button
              onClick={approveInvoice}
              disabled={approving || saving || lineItems.length === 0}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="approve-invoice-btn"
              title="Authorise in Xero — flips status from DRAFT to AUTHORISED so it leaves the Drafts tab and a Review-and-pay link is issued"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {approving ? 'Authorising…' : 'Approve'}
            </Button>
          )}
          {isDraft && (
            <Button
              onClick={markSent}
              disabled={sending || lineItems.length === 0}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="mark-sent-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              {sending ? 'Locking…' : isPushed ? 'Mark as Sent' : 'Lock locally'}
            </Button>
          )}
          {isPushed && (
            <Button
              onClick={refreshFromXero}
              disabled={refreshing || saving || pushing}
              variant="outline"
              className="border-sky-300 text-sky-800 hover:bg-sky-50"
              data-testid="refresh-from-xero-btn"
              title="Pull the latest state of this invoice from Xero — line items, totals, dates and status."
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh from Xero'}
            </Button>
          )}
          {isPushed && (
            <a
              href={`https://go.xero.com/app/invoicing/view/${invoice.xeroInvoiceId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="open-in-xero-link"
            >
              <ExternalLink className="h-4 w-4" /> Open in Xero
            </a>
          )}
          {/* Manual payment thank-you — REPLACES the previous auto-send
              on the PAID transition. Only appears once the invoice is
              PAID in Xero and hasn't been sent yet. */}
          {invoice.xeroStatus === 'PAID' && !invoice.paidThankYouSentAt && (
            <Button
              onClick={async () => {
                const label = invoice.lastEmailedTo || (invoice.emailHistory?.[invoice.emailHistory.length - 1]?.to?.join(', ')) || 'the customer';
                if (!window.confirm(`Send a "thank you for payment" email to ${label}?`)) return;
                try {
                  await sendPaymentThankYou(invoice.id);
                  toast.success('Thank-you email sent.');
                  const fresh = await getInvoice(invoice.id);
                  setInvoice(fresh);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not send thank-you email');
                }
              }}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="send-thankyou-btn"
              title="Send a payment thank-you email to the invoice recipient. This never fires automatically."
            >
              <Mail className="h-4 w-4 mr-1" /> Send thank-you
            </Button>
          )}
          {invoice.paidThankYouSentAt && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium"
              data-testid="thankyou-sent-pill"
              title={`Thank-you sent at ${invoice.paidThankYouSentAt}`}
            >
              <CheckCircle2 className="h-3 w-3" /> Thank-you sent
            </span>
          )}
        </div>
      </div>

      {isDraft && !xeroConnected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-800">
          Xero isn&rsquo;t connected — go to{' '}
          <Link to="/admin/xero" className="underline font-semibold">Xero settings</Link>{' '}
          to enable one-click push.
        </div>
      )}

      {!isDraft && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-700">
          <Lock className="inline h-4 w-4 mr-1" />
          This invoice is {invoice.sent ? 'sent' : `in status ${invoice.status}`} and can&rsquo;t
          be edited from here. Use the Xero link in the list if you need to make changes.
        </div>
      )}

      {isDraft && (
        <PendingDiscountsPanel
          invoice={invoice}
          setInvoice={setInvoice}
          setLineItems={setLineItems}
        />
      )}

      {invoice.looksCorrupted && (
        <CorruptedInvoiceBanner invoice={invoice} navigate={navigate} />
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-sm font-semibold text-slate-900">Line items</div>
          <div className="text-xs text-slate-500">{lineItems.length} item{lineItems.length === 1 ? '' : 's'}</div>
        </div>

        {lineItems.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500 text-sm">
            No line items. Add one below to start building this invoice.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lineGroups.map(([woId, group]) => (
              <li key={woId || '_ungrouped'} className={woId ? 'bg-slate-50/50' : ''}>
                {woId && (
                  <div className="flex items-center justify-between gap-2 px-4 py-2 bg-slate-100/60 border-b border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-700 min-w-0">
                      <Link
                        to={`/admin/work-orders?id=${woId}`}
                        className="font-mono font-semibold hover:underline truncate"
                      >
                        {woId}
                      </Link>
                      <span className="text-slate-500 hidden sm:inline">
                        · {group.length} line{group.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {isDraft && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50 hover:text-amber-900 shrink-0"
                        onClick={() => setDetachWoId(woId)}
                        data-testid={`detach-wo-btn-${woId}`}
                        title={`Detach ${woId} from this invoice`}
                      >
                        <Unlink2 className="h-3.5 w-3.5 mr-1" /> Detach
                      </Button>
                    )}
                  </div>
                )}
                <ul className="divide-y divide-slate-100">
                  {group.map(({ li, idx }) => (
                    <LineItemRow
                      key={idx}
                      line={li}
                      readOnly={!isDraft}
                      onChange={(patch) => updateLine(idx, patch)}
                      onRemove={() => removeLine(idx)}
                      testIdx={idx}
                    />
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}

        {isDraft && (
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
            <Button
              variant="outline"
              size="sm"
              onClick={addLine}
              data-testid="add-line-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add line
            </Button>
          </div>
        )}

        <div className="px-4 py-3 border-t border-slate-200 bg-white space-y-1.5" data-testid="invoice-totals">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Subtotal (net)</span>
            <span className="tabular-nums" data-testid="invoice-subtotal">£{totals.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>VAT</span>
            <span className="tabular-nums" data-testid="invoice-vat-total">£{totals.vatTotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
            <div className="text-sm font-semibold text-slate-700">Total</div>
            <div className="font-display text-2xl tabular-nums text-slate-900" data-testid="invoice-total">
              £{totals.grandTotal.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">
            Due date
          </label>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => { setDueDate(e.target.value); setDirty(true); }}
            disabled={!isDraft}
            data-testid="invoice-due-date"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">
            Notes
          </label>
          <Input
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
            disabled={!isDraft}
            placeholder="Optional — shown on the invoice"
            data-testid="invoice-notes"
          />
        </div>
      </div>

      {/* Detach-WO confirmation */}
      <Dialog
        open={!!detachWoId}
        onOpenChange={(o) => { if (!o && !detaching) setDetachWoId(null); }}
      >
        <DialogContent data-testid="detach-wo-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Detach <span className="font-mono">{detachWoId}</span> from this invoice?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Removes every line referencing this work order, recomputes the
              invoice total, and frees the WO so it can be re-billed elsewhere.
              Other work orders on this invoice are untouched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDetachWoId(null)}
              disabled={detaching}
              data-testid="detach-wo-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => detachWO(detachWoId)}
              disabled={detaching}
              data-testid="detach-wo-confirm"
            >
              {detaching ? 'Detaching…' : 'Detach work order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Renumber draft invoice. Cascades the new id through every FK
          reference (work_orders.invoiceId, time_entries.billedInvoiceId,
          partsUsed[].billedInvoiceId) so nothing dangles. Only available
          while the draft has not yet been pushed to Xero. */}
      <Dialog
        open={renameOpen}
        onOpenChange={(o) => { if (!o && !renameSaving) setRenameOpen(false); }}
      >
        <DialogContent data-testid="rename-invoice-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renumber {isPushed ? 'invoice' : 'draft invoice'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              {isPushed ? (
                <>
                  Push a new InvoiceNumber to Xero (only possible while the
                  invoice is still <span className="font-mono">DRAFT</span>).
                  The local id and every reference (work orders, time
                  entries, parts) will be updated to match.
                </>
              ) : (
                <>
                  Set the desired Xero invoice number now (e.g. <span className="font-mono">IN2026000024</span>).
                  Both the local id and the Xero number will be set to this value, and every
                  reference (work orders, time entries, parts) will be cascaded.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-invoice-input" className="text-xs uppercase tracking-wider text-slate-500">
              New invoice number
            </Label>
            <Input
              id="rename-invoice-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !renameSaving) submitRename(); }}
              placeholder="IN2026000024"
              autoFocus
              spellCheck={false}
              className="font-mono"
              data-testid="rename-invoice-input"
            />
            <p className="text-[11px] text-slate-500">
              Allowed: letters, digits, dash, underscore. Max 60 chars.
            </p>
          </div>
          <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setRenameOpen(false)}
              disabled={renameSaving}
              data-testid="rename-invoice-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRename}
              disabled={renameSaving || !renameValue.trim()}
              data-testid="rename-invoice-confirm"
            >
              {renameSaving ? 'Renumbering…' : 'Renumber'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Email invoice to customer */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent
          data-testid="email-invoice-dialog"
          className="max-w-lg w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden"
        >
          <DialogHeader>
            <DialogTitle>Email {xeroNumber || invoice.id} to customer</DialogTitle>
            <DialogDescription>
              Sends the {isPushed || (xeroConnected && emailPushFirst) ? 'Xero-hosted' : 'Lutrom-branded'} PDF as an attachment.
              {emailLockAfter ? ' The draft will be locked after sending.' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* TO / CC stacks — visible chips for what's been picked */}
            <div className="space-y-2">
              <RecipientStack
                label="To"
                emails={emailTo}
                onRemove={removeEmail}
                accent="emerald"
                testid="email-invoice-to"
              />
              <RecipientStack
                label="Cc"
                emails={emailCc}
                onRemove={removeEmail}
                accent="amber"
                testid="email-invoice-cc"
              />
            </div>

            {/* Manual entry — free-form input + To / Cc dispatch buttons. */}
            <div>
              <Label htmlFor="email-invoice-manual">Add an email</Label>
              <div className="flex gap-1.5 mt-1.5">
                <Input
                  id="email-invoice-manual"
                  type="email"
                  value={emailManual}
                  onChange={(e) => setEmailManual(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addManual('to');
                    }
                  }}
                  placeholder="name@company.com"
                  className="flex-1 min-w-0"
                  data-testid="email-invoice-manual"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addManual('to')}
                  disabled={!emailManual.trim()}
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 px-3"
                  data-testid="email-invoice-manual-to"
                >
                  → To
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addManual('cc')}
                  disabled={!emailManual.trim()}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50 px-3"
                  data-testid="email-invoice-manual-cc"
                >
                  → Cc
                </Button>
              </div>
            </div>

            {/* Customer-team contact picker — each row has To / Cc buttons. */}
            {emailContacts.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Customer contacts
                </div>
                <div className="divide-y divide-slate-100">
                  {emailContacts.map((c) => {
                    const bucket = emailBucket(c.email);
                    return (
                      <div
                        key={c.id}
                        className="flex items-center gap-1.5 px-2.5 py-2 sm:gap-2 sm:px-3"
                        data-testid={`email-invoice-contact-${c.id}`}
                      >
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900 truncate">
                            {c.isPrimary && (
                              <span className="text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Primary
                              </span>
                            )}
                            {!c.isPrimary && (
                              <span
                                className="text-[9px] uppercase tracking-wider font-semibold px-1 py-px rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                                title="Sub-customer under the primary payer"
                              >
                                Sub
                              </span>
                            )}
                            <span className="truncate">{c.name || c.email}</span>
                          </div>
                          {c.name && (
                            <div className="text-xs text-slate-500 truncate">{c.email}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => assignEmail(c.email, 'to')}
                          className={
                            'shrink-0 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors '
                            + (bucket === 'to'
                              ? 'bg-emerald-700 border-emerald-700 text-white'
                              : 'border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-800')
                          }
                          data-testid={`email-invoice-contact-${c.id}-to`}
                        >
                          To
                        </button>
                        <button
                          type="button"
                          onClick={() => assignEmail(c.email, 'cc')}
                          className={
                            'shrink-0 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors '
                            + (bucket === 'cc'
                              ? 'bg-amber-600 border-amber-600 text-white'
                              : 'border-slate-300 text-slate-600 hover:border-amber-500 hover:text-amber-800')
                          }
                          data-testid={`email-invoice-contact-${c.id}-cc`}
                        >
                          Cc
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email-invoice-note">Optional note</Label>
              <textarea
                id="email-invoice-note"
                rows={3}
                className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="e.g. Thanks for the job — remittance advice welcome when paid."
                data-testid="email-invoice-note"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={emailCcAdmin}
                  onCheckedChange={(v) => setEmailCcAdmin(!!v)}
                  data-testid="email-invoice-cc-admin"
                />
                CC admin address
              </label>
              {xeroConnected && !isPushed && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox
                    checked={emailPushFirst}
                    onCheckedChange={(v) => setEmailPushFirst(!!v)}
                    data-testid="email-invoice-push-first"
                  />
                  Push to Xero first (attaches the Xero-hosted PDF)
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={emailLockAfter}
                  onCheckedChange={(v) => setEmailLockAfter(!!v)}
                  data-testid="email-invoice-lock-after"
                />
                Lock invoice after sending (future clock-outs start a new draft)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button
              type="button"
              variant="outline"
              onClick={openPreview}
              disabled={emailSending}
              className="border-slate-300 text-slate-800 hover:bg-slate-50"
              data-testid="email-invoice-preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview email &amp; PDF
            </Button>
            <Button
              onClick={sendEmail}
              disabled={emailSending}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="email-invoice-send-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {emailSending ? 'Sending…' : 'Send invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Invoice preview"
      />

      {/* Pre-send preview dialog. Shows the rendered email subject +
          HTML body the customer will see, the resolved sender / cc-admin
          headers, and an iframe with the actual PDF that will be
          attached. The "Send invoice" button mirrors the email
          dialog's primary action so the admin can confirm-and-send
          without bouncing back to the previous dialog. */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent
          data-testid="email-invoice-preview-dialog"
          className="max-w-6xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Email preview · {previewData?.displayNumber || invoice.id}</DialogTitle>
            <DialogDescription>
              This is exactly what the customer will receive when you tap Send. Edit the note, recipients or attached PDF first if anything looks wrong.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !previewData ? (
            <div className="py-12 text-center text-slate-500 text-sm" data-testid="email-invoice-preview-loading">
              Loading preview…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header block summarising who's getting what */}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5" data-testid="email-invoice-preview-headers">
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">From</span><span className="text-slate-900 font-medium">{previewData.sender}</span></div>
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">To</span><span className="text-slate-900 font-medium">{emailTo.join(', ') || <em className="text-slate-400">(none yet)</em>}</span></div>
                {(emailCc.length > 0 || previewData.adminCc) && (
                  <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">Cc</span><span className="text-slate-900">
                    {[...emailCc, previewData.adminCc].filter(Boolean).join(', ')}
                  </span></div>
                )}
                <div className="flex gap-2"><span className="w-16 text-slate-500 uppercase tracking-wider">Subject</span><span className="text-slate-900 font-semibold">{previewData.subject}</span></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Email body — rendered HTML inside an iframe so the
                    customer-facing styles (Lutrom branding, Resend
                    template) render exactly as the customer will see
                    them, without leaking any portal CSS. Toggling
                    "Edit body" swaps to a plain-HTML textarea so the
                    admin can tweak copy before sending. */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      Email body
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingBody((v) => !v)}
                      className="text-[11px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                      data-testid="email-invoice-preview-edit-toggle"
                    >
                      {editingBody ? '← Back to preview' : 'Edit body'}
                    </button>
                  </div>
                  {editingBody ? (
                    <>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Subject line</Label>
                          <Input
                            value={editedSubject}
                            onChange={(e) => setEditedSubject(e.target.value)}
                            className="mt-1 h-8 text-sm"
                            data-testid="email-invoice-preview-subject-editor"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                            Greeting <span className="text-slate-400 normal-case">(the "Hi X," line)</span>
                          </Label>
                          <Input
                            value={editedGreetingLine}
                            onChange={(e) => setEditedGreetingLine(e.target.value)}
                            placeholder={`Hi ${deriveGreetingName() || 'there'},`}
                            className="mt-1 h-8 text-sm"
                            data-testid="email-invoice-preview-greeting-editor"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Body copy</Label>
                          <Textarea
                            value={editedBodyText}
                            onChange={(e) => setEditedBodyText(e.target.value)}
                            className="w-full h-[38vh] text-sm leading-relaxed mt-1"
                            placeholder="Blank line = new paragraph."
                            data-testid="email-invoice-preview-body-editor"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Button label</Label>
                            <Input
                              value={editedButtonLabel}
                              onChange={(e) => setEditedButtonLabel(e.target.value)}
                              className="mt-1 h-8 text-sm"
                              data-testid="email-invoice-preview-button-editor"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sign-off</Label>
                            <Input
                              value={editedCloser}
                              onChange={(e) => setEditedCloser(e.target.value)}
                              className="mt-1 h-8 text-sm"
                              data-testid="email-invoice-preview-closer-editor"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 flex items-center justify-between mt-1">
                        <span>Every visible line is editable — Lutrom logo + PDF attachment stay in place.</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditedBodyText(previewData.defaultBodyText || '');
                            setEditedSubject(previewData.defaultSubject || '');
                            setEditedGreetingLine(previewData.defaultGreeting || '');
                            setEditedButtonLabel(previewData.defaultButtonLabel || '');
                            setEditedCloser(previewData.defaultCloser || '');
                          }}
                          className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                          data-testid="email-invoice-preview-body-reset"
                        >
                          Reset to template
                        </button>
                      </div>
                    </>
                  ) : (
                    <iframe
                      srcDoc={previewData.html}
                      title="Invoice email preview"
                      className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
                      sandbox=""
                      data-testid="email-invoice-preview-body-iframe"
                    />
                  )}
                </div>

                {/* Attached PDF preview */}
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                    <span>Attached PDF · {previewPdfName || `${invoice.id}.pdf`}</span>
                    {previewPdfUrl && (
                      <a
                        href={previewPdfUrl}
                        download={previewPdfName || `${invoice.id}.pdf`}
                        className="text-[11px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2 normal-case tracking-normal"
                        data-testid="email-invoice-preview-pdf-download"
                      >
                        Download
                      </a>
                    )}
                  </div>
                  {previewPdfUrl ? (
                    <iframe
                      src={previewPdfUrl}
                      title="Invoice PDF preview"
                      className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
                      data-testid="email-invoice-preview-pdf-iframe"
                    />
                  ) : (
                    <div className="w-full h-[60vh] border border-dashed border-slate-300 rounded-md flex items-center justify-center text-xs text-slate-500">
                      Could not render PDF preview.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closePreview}
              data-testid="email-invoice-preview-close"
            >
              Back to recipients
            </Button>
            <Button
              onClick={async () => { closePreview(); await sendEmail(); }}
              disabled={emailSending || previewLoading || !previewData}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="email-invoice-preview-send-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {emailSending ? 'Sending…' : 'Send invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LineItemRow = ({ line, readOnly, onChange, onRemove, testIdx }) => {
  const Icon = line.type === 'labor' ? Home : line.type === 'part' ? Package : Building2;
  const net = (Number(line.quantity) || 0) * (Number(line.unit_amount) || 0);
  // Same fallback logic as the parent totals — undefined/null/'' →
  // 20% default; explicit `0` = zero-rated.
  const rate = (line.taxRate === undefined || line.taxRate === null || line.taxRate === '')
    ? 0.20
    : Number(line.taxRate) || 0;
  const vat = net * rate;
  const gross = net + vat;
  return (
    <li className="px-4 py-3 space-y-2" data-testid={`line-item-${testIdx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <Icon className="h-3.5 w-3.5 text-slate-400" />
          {line.type || 'line'}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold tabular-nums text-slate-900" data-testid={`line-gross-${testIdx}`}>
            £{gross.toFixed(2)}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onRemove}
              className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
              data-testid={`remove-line-${testIdx}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <Input
        value={line.description || ''}
        disabled={readOnly}
        onChange={(e) => onChange({ description: e.target.value })}
        placeholder="Description"
        data-testid={`line-description-${testIdx}`}
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-xs">
          <span className="text-slate-500">Qty</span>
          <Input
            type="number"
            step="0.25"
            min="0"
            className="mt-0.5 h-9"
            value={line.quantity ?? 0}
            disabled={readOnly}
            onChange={(e) => onChange({ quantity: e.target.value === '' ? 0 : Number(e.target.value) })}
            data-testid={`line-qty-${testIdx}`}
          />
        </label>
        <label className="text-xs">
          <span className="text-slate-500">Unit (£, net)</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            className="mt-0.5 h-9"
            value={line.unit_amount ?? 0}
            disabled={readOnly}
            onChange={(e) => onChange({ unit_amount: e.target.value === '' ? 0 : Number(e.target.value) })}
            data-testid={`line-unit-${testIdx}`}
          />
        </label>
        <label className="text-xs">
          <span className="text-slate-500">VAT</span>
          <select
            className="mt-0.5 h-9 w-full border border-slate-300 rounded-md text-sm px-2 disabled:opacity-60"
            value={String(rate)}
            disabled={readOnly}
            onChange={(e) => onChange({ taxRate: Number(e.target.value) })}
            data-testid={`line-tax-rate-${testIdx}`}
          >
            <option value="0.2">20%</option>
            <option value="0.05">5%</option>
            <option value="0">0% / Exempt</option>
          </select>
        </label>
        <div className="text-xs">
          <span className="text-slate-500">Line total</span>
          <div className="mt-0.5 h-9 px-2 flex items-center justify-end tabular-nums bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-800">
            £{net.toFixed(2)}
            <span className="text-[10px] text-slate-500 ml-1.5">
              +£{vat.toFixed(2)} VAT
            </span>
          </div>
        </div>
      </div>
      {(line.timeEntryId || line.workOrderId) && (
        <div className="flex items-center gap-1 text-[10px] text-slate-400 flex-wrap">
          <Pencil className="h-2.5 w-2.5" />
          Source:
          {line.workOrderId && <span>WO {line.workOrderId}</span>}
          {line.timeEntryId && <span>time {line.timeEntryId}</span>}
        </div>
      )}
    </li>
  );
};

export default AdminInvoiceDetail;

// ---- Corrupted-invoice repair banner ----
// Shows on invoices where the local id was clobbered by the pre-fix
// Xero polling reference-collision bug (local id INV-2826 but
// xeroInvoiceNumber is IN20230xxx = an unrelated old Xero invoice).
// One click: detach from Xero + rebuild from the original time entries.
const CorruptedInvoiceBanner = ({ invoice, navigate }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const doRepair = async () => {
    setBusy(true);
    try {
      const res = await rebuildInvoiceFromSource(invoice.id);
      if (res.newInvoiceIds && res.newInvoiceIds.length > 0) {
        toast.success(`Rebuilt · ${res.entriesReleased} entries → ${res.newInvoiceIds.length} draft${res.newInvoiceIds.length === 1 ? '' : 's'}`);
        navigate(`/admin/invoices/${res.newInvoiceIds[0]}`);
      } else if (res.skipped && res.skipped.length > 0) {
        toast.error(`Removed ${invoice.id}, but the source entries couldn't be re-billed (${res.skipped[0].reason || res.skipped[0].error || 'unknown'}). Check the Timesheets → Skipped tab.`, { duration: 10000 });
        navigate('/admin/invoices');
      } else {
        toast.success('Repaired');
        navigate('/admin/invoices');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-rose-50 border border-rose-300 rounded-md p-4 space-y-3"
      data-testid="corrupted-invoice-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-rose-900">
            This invoice looks corrupted
          </div>
          <div className="text-xs text-rose-800 mt-1 leading-relaxed">
            The local id <strong>{invoice.id}</strong> is bound to Xero invoice{' '}
            <strong>{invoice.xeroInvoiceNumber}</strong> — an older invoice that
            reused the same Reference. The Xero polling job silently overwrote this
            row&rsquo;s line items before we fixed the collision guard. Anything you see
            below is <em>not</em> your original {invoice.id} — it belongs to{' '}
            {invoice.xeroInvoiceNumber}.
          </div>
          <div className="text-xs text-rose-800 mt-2">
            <strong>Rebuild from source</strong> will detach this row from Xero,
            release the underlying time entries, and re-run the auto-biller so
            you get a clean new draft(s) with the ACTUAL hours that were originally
            logged. Xero-side {invoice.xeroInvoiceNumber} stays untouched.
          </div>
        </div>
      </div>
      {!confirm ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            className="bg-rose-700 hover:bg-rose-800 text-white"
            onClick={() => setConfirm(true)}
            data-testid="corrupted-repair-btn"
          >
            Rebuild from source&hellip;
          </Button>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirm(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-rose-700 hover:bg-rose-800 text-white"
            onClick={doRepair}
            disabled={busy}
            data-testid="corrupted-repair-confirm"
          >
            {busy ? 'Rebuilding…' : `Yes, rebuild ${invoice.id}`}
          </Button>
        </div>
      )}
    </div>
  );
};

// ---- Pending discounts panel ----
// Surfaced on draft invoices when the customer has un-applied
// early-payment discount credits waiting. Admin picks which credits to
// apply as negative line items on this invoice; unpicked credits stay
// pending for a future draft. Also shows currently-applied credits
// with an "Undo" pill so mistakes can be reversed while still a draft.
const PendingDiscountsPanel = ({ invoice, setInvoice, setLineItems }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(null); // creditId currently un-applying
  const [discarding, setDiscarding] = useState(null); // creditId currently being discarded

  const available = invoice.availableDiscounts || [];
  // Currently-applied credits (rendered by scanning line items for
  // `discountCreditId`). Kept alongside available so the admin sees
  // both in one place.
  // One row per credit (a credit may span several VAT-rate lines); amount = net + VAT reduction.
  const applied = Object.values((invoice.lineItems || [])
    .filter(li => li.discountCreditId)
    .reduce((acc, li) => {
      const rate = (li.taxRate === undefined || li.taxRate === null || li.taxRate === '') ? 0.2 : Number(li.taxRate) || 0;
      const net = -(Number(li.quantity || 1) * Number(li.unit_amount || 0));
      const row = acc[li.discountCreditId] || { creditId: li.discountCreditId, description: li.description.replace(/ — [\d.]+% VAT$/, ''), net: 0, vat: 0 };
      row.net += net;
      row.vat += net * rate;
      acc[li.discountCreditId] = row;
      return acc;
    }, {}))
    .map(a => ({ ...a, amount: Math.round((a.net + a.vat) * 100) / 100 }));

  if (available.length === 0 && applied.length === 0) return null;

  const toggle = (creditId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(creditId)) next.delete(creditId);
      else next.add(creditId);
      return next;
    });
  };

  const applySelected = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    try {
      const res = await applyDiscountsToInvoice(invoice.id, Array.from(selected));
      if (res.invoice) {
        setInvoice(res.invoice);
        setLineItems(res.invoice.lineItems || []);
      }
      setSelected(new Set());
      toast.success(`Applied ${res.applied} discount${res.applied === 1 ? '' : 's'} · £${Math.abs(res.addedAmount).toFixed(2)} off`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply discounts');
    } finally {
      setApplying(false);
    }
  };

  const undo = async (creditId) => {
    setUndoing(creditId);
    try {
      const res = await unapplyDiscountFromInvoice(invoice.id, creditId);
      if (res.invoice) {
        setInvoice(res.invoice);
        setLineItems(res.invoice.lineItems || []);
      }
      toast.success('Discount removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove discount');
    } finally {
      setUndoing(null);
    }
  };

  // Discard a PENDING credit — used when the discount was already
  // applied manually elsewhere (e.g. inside Xero) so it should stop
  // showing up on future drafts. Removes the row from the
  // `discount_credits` collection permanently and scrubs it from any
  // invoice's `availableDiscounts` cache. Applied credits are blocked
  // server-side (must be un-applied first).
  const discard = async (creditId) => {
    if (!window.confirm('Remove this pending credit permanently? Use this when the discount was already applied manually (e.g. in Xero).')) return;
    setDiscarding(creditId);
    try {
      await discardPendingDiscountCredit(creditId);
      // Optimistic client-side removal — the backend also scrubs
      // `availableDiscounts` but we don't wait for a reload.
      setInvoice(prev => ({
        ...prev,
        availableDiscounts: (prev.availableDiscounts || []).filter(d => d.creditId !== creditId),
      }));
      setSelected(prev => {
        if (!prev.has(creditId)) return prev;
        const next = new Set(prev); next.delete(creditId); return next;
      });
      toast.success('Pending credit discarded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to discard credit');
    } finally {
      setDiscarding(null);
    }
  };

  const selectedTotal = available
    .filter(d => selected.has(d.creditId))
    .reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <div
      className="bg-emerald-50 border border-emerald-200 rounded-md overflow-hidden"
      data-testid="pending-discounts-panel"
    >
      <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-100/50 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
          <CheckCircle2 className="h-4 w-4" />
          Early-payment discount credits
        </div>
        <div className="text-[11px] text-emerald-800">
          {available.length} pending · {applied.length} applied
        </div>
      </div>

      {applied.length > 0 && (
        <ul className="divide-y divide-emerald-100/60 bg-white">
          {applied.map(a => (
            <li
              key={a.creditId}
              className="px-4 py-2 flex items-center gap-3 text-sm"
              data-testid={`applied-discount-${a.creditId}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 truncate">{a.description}</div>
              </div>
              <div className="tabular-nums font-medium text-emerald-800 text-right" data-testid={`applied-discount-amount-${a.creditId}`}>
                −£{a.amount.toFixed(2)}
                <div className="text-[10px] font-normal text-slate-500">£{a.net.toFixed(2)} net + £{a.vat.toFixed(2)} VAT</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() => undo(a.creditId)}
                disabled={undoing === a.creditId}
                data-testid={`undo-discount-${a.creditId}`}
              >
                {undoing === a.creditId ? 'Undoing…' : 'Undo'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 && (
        <div className="p-3 space-y-2 bg-white">
          <div className="text-xs text-slate-600 flex items-start gap-1.5">
            <History className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
            <span>
              These credits were queued when the customer paid earlier invoices inside their early-payment window. Tick which ones to attach to this draft as a negative line — the rest stay pending for future invoices.
            </span>
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
            {available.map(d => (
              <li
                key={d.creditId}
                className={`px-3 py-2 flex items-center gap-3 text-sm cursor-pointer transition-colors ${selected.has(d.creditId) ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                onClick={() => toggle(d.creditId)}
                data-testid={`available-discount-${d.creditId}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(d.creditId)}
                  onChange={() => toggle(d.creditId)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                  data-testid={`discount-check-${d.creditId}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 truncate">
                    Invoice {d.sourceXeroNumber || d.sourceInvoiceId || '?'} paid in {d.paidInDays}d
                  </div>
                  <div className="text-[11px] text-slate-500" data-testid={`available-discount-basis-${d.creditId}`}>
                    {d.sourceNet != null
                      ? <>{d.percent}% of £{Number(d.sourceNet).toFixed(2)} net · £{Number(d.netAmount || 0).toFixed(2)} + £{Number(d.vatAmount || 0).toFixed(2)} VAT</>
                      : <>{d.percent}% of £{Number(d.sourceTotal || 0).toFixed(2)}</>}
                  </div>
                </div>
                <div className="tabular-nums font-medium text-emerald-700">
                  −£{Number(d.amount || 0).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); discard(d.creditId); }}
                  disabled={discarding === d.creditId}
                  className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0 disabled:opacity-50"
                  data-testid={`discard-discount-${d.creditId}`}
                  title="Remove — use when this credit was already applied manually elsewhere"
                  aria-label={`Discard pending credit for ${d.sourceXeroNumber || d.sourceInvoiceId}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-500">
              {selected.size > 0
                ? <>Reduces invoice by <strong className="text-emerald-700 tabular-nums">£{selectedTotal.toFixed(2)}</strong></>
                : 'Nothing selected'}
            </div>
            <Button
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={applySelected}
              disabled={selected.size === 0 || applying}
              data-testid="apply-discounts-btn"
            >
              {applying ? 'Applying…' : `Apply ${selected.size || ''} discount${selected.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
